import { WithLoader } from 'Components/WithLoader'
import { ListSubheader, useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import * as React from 'react'
import { useEffect } from 'react'
import { t } from 'ttag'

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white'
}))

// This function is used to get the difference between two arrays
function not(a: string[], b: string[]) {
  return a.filter(value => b.indexOf(value) === -1)
}

// This function is used to get the intersection between two arrays
function intersection(a: string[], b: string[]) {
  return a.filter(value => b.indexOf(value) !== -1)
}

interface CustomTransferListWithoutActionButtonsProps {
  leftItemsHeaderText: string
  rightItemsHeaderText: string
  leftItems: string[]
  rightItems: string[]
  renderItemContent: (itemId: string) => React.ReactNode
  optionsLoading: boolean
  handleItemsChange: (right: string[]) => void
  allowTransferBack?: boolean
  customStyles?: React.CSSProperties
}

function CustomTransferListWithoutActionButtons({
  leftItemsHeaderText,
  rightItemsHeaderText,
  leftItems,
  rightItems,
  renderItemContent,
  optionsLoading,
  handleItemsChange,
  allowTransferBack = true,
  customStyles
}: Readonly<CustomTransferListWithoutActionButtonsProps>) {
  const theme = useTheme()
  const [checked, setChecked] = React.useState<string[]>([])
  const [left, setLeft] = React.useState<string[]>(leftItems)
  const [right, setRight] = React.useState<string[]>(rightItems)

  const leftChecked = intersection(checked, left)
  const rightChecked = intersection(checked, right)

  useEffect(() => {
    // Remove the items from left items that are already in the right items.
    const leftItemsWithRightItemsExcluded = not(leftItems, right)
    setLeft(leftItemsWithRightItemsExcluded)
  }, [leftItems])

  useEffect(() => {
    handleItemsChange(right)
  }, [right])

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const handleAllRight = () => {
    setRight(left.concat(right))
    setLeft([])
  }

  const handleCheckedRight = () => {
    setRight(leftChecked.concat(right))
    setLeft(not(left, leftChecked))
    setChecked(not(checked, leftChecked))
  }

  const handleCheckedLeft = () => {
    setLeft(rightChecked.concat(left))
    setRight(not(right, rightChecked))
    setChecked(not(checked, rightChecked))
  }

  const handleAllLeft = () => {
    setLeft(right.concat(left))
    setRight([])
  }

  const customList = (items: string[], listSubHeaderText: string, optionsLoading = false) => (
    <Paper
      sx={{
        width: '100%',
        height: 500,
        overflow: 'auto',
        ...(theme.palette.mode === 'light' && { background: 'white' }),
        ...customStyles
      }}
    >
      <List
        dense
        component="div"
        subheader={<StyledListSubheader>{listSubHeaderText}</StyledListSubheader>}
      >
        <WithLoader loading={optionsLoading}>
          {items.map((value: string) => {
            return (
              <ListItemButton
                selected={checked.indexOf(value) !== -1}
                key={value}
                onClick={handleToggle(value)}
              >
                {renderItemContent(value)}
              </ListItemButton>
            )
          })}
        </WithLoader>
      </List>
    </Paper>
  )

  return (
    <Grid container spacing={2} sx={{ py: theme.spacing(2), alignItems: 'center' }}>
      <Grid size={5}>{customList(left, leftItemsHeaderText, optionsLoading)}</Grid>
      <Grid size={2}>
        <Grid container direction="column" sx={{ alignItems: 'center' }}>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            color="inherit"
            onClick={handleAllRight}
            disabled={left.length === 0}
            aria-label={t`Move all right`}
          >
            ≫
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            color="inherit"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label={t`Move selected right`}
          >
            &gt;
          </Button>
          {allowTransferBack && (
            <>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label={t`Move selected left`}
              >
                &lt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleAllLeft}
                disabled={right.length === 0}
                aria-label={t`Move all left`}
              >
                ≪
              </Button>
            </>
          )}
        </Grid>
      </Grid>
      <Grid size={5}>{customList(right, rightItemsHeaderText)}</Grid>
    </Grid>
  )
}

export default CustomTransferListWithoutActionButtons
